import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff, ShieldCheck, ArrowRight, CheckCircle } from 'lucide-react';
import { post } from '../../lib/apiClient';
import { API_URL } from '../../_config';
import { generateIdempotencyKey } from '../../lib/idempotency';

export default function ResetPasswordPage() {
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const email = searchParams.get('email') ?? '';

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!newPassword) { setError('Please enter a new password'); return; }
    if (newPassword !== confirmPassword) { setError('Passwords do not match'); return; }
    if (!token || !email) { setError('Invalid reset link. Please request a new one.'); return; }
    setLoading(true);
    setError('');
    try {
      await post(API_URL.AUTH.RESET_PASSWORD, {
        token,
        email,
        password: newPassword,
        password_confirmation: confirmPassword,
      }, { headers: { 'Idempotency-Key': generateIdempotencyKey() } });
      setSuccess(true);
    } catch (err: any) {
      setError(err.message ?? 'Password reset failed. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rp-wrap">
      <div className="rp-left">
        <div className="rp-left-dots" />
        <div className="rp-left-glow" />
        <div className="rp-left-inner">
          <Link to="/" className="rp-logo-link">
            <img src="/images/logo.png" alt="LeadNova" className="rp-logo" />
          </Link>
          <div className="rp-icon-box">
            <ShieldCheck size={36} color="#4DB8E8" />
          </div>
          <h1 className="rp-left-title">Secure your account</h1>
          <p className="rp-left-desc">
            Choose a strong password with at least 8 characters including uppercase, lowercase, numbers and special characters.
          </p>
        </div>
      </div>

      <div className="rp-right">
        <div className="rp-right-inner">
          <div className="rp-mobile-logo">
            <Link to="/"><img src="/images/logo.png" alt="LeadNova" className="rp-logo-sm" /></Link>
          </div>
          {!success ? (
            <>
              <div className="rp-form-header">
                <h2 className="rp-form-title">Set new password</h2>
                <p className="rp-form-subtitle">Your new password must be at least 8 characters long.</p>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="rp-field">
                  <label className="rp-label">New password</label>
                  <div className="rp-input-wrap">
                    <input className="rp-input" type={showNew ? 'text' : 'password'} placeholder="Enter new password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
                    <button type="button" onClick={() => setShowNew(!showNew)} className="rp-eye-btn">
                      {showNew ? <EyeOff size={18} color="var(--gray-500)" /> : <Eye size={18} color="var(--gray-500)" />}
                    </button>
                  </div>
                </div>
                <div className="rp-field">
                  <label className="rp-label">Confirm new password</label>
                  <div className="rp-input-wrap">
                    <input className="rp-input" type={showConfirm ? 'text' : 'password'} placeholder="Confirm new password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} />
                    <button type="button" onClick={() => setShowConfirm(!showConfirm)} className="rp-eye-btn">
                      {showConfirm ? <EyeOff size={18} color="var(--gray-500)" /> : <Eye size={18} color="var(--gray-500)" />}
                    </button>
                  </div>
                </div>
                {error && <div className="rp-error">{error}</div>}
                <button type="submit" className="rp-submit-btn" disabled={loading}>
                  {loading ? 'Resetting…' : 'Reset Password'} {!loading && <ArrowRight size={18} />}
                </button>
              </form>
              <p className="rp-switch">
                Remember your password? <Link to="/login">Log in</Link>
              </p>
            </>
          ) : (
            <div className="rp-success">
              <div className="rp-success-icon">
                <CheckCircle size={28} color="var(--green)" />
              </div>
              <h2 className="rp-success-title">Password updated!</h2>
              <p className="rp-success-desc">
                Your password has been successfully reset. You can now log in with your new password.
              </p>
              <Link to="/login" className="rp-submit-btn" style={{ textDecoration: 'none' }}>
                Go to Login <ArrowRight size={18} />
              </Link>
            </div>
          )}
        </div>
      </div>

      <style>{`
        .rp-wrap { min-height: calc(100vh - 72px); display: flex; }

        .rp-left {
          flex: 0 0 50%; position: relative; overflow: hidden;
          background: linear-gradient(135deg, #1A2D5A 0%, #243A6C 40%, #1c3562 100%);
          display: flex; flex-direction: column; justify-content: center; align-items: center;
          padding: 60px 56px;
        }
        .rp-left-dots {
          position: absolute; inset: 0; opacity: 0.05;
          background-image: radial-gradient(circle, #fff 1px, transparent 1px);
          background-size: 24px 24px;
        }
        .rp-left-glow {
          position: absolute; bottom: -100px; right: -100px; width: 400px; height: 400px;
          background: radial-gradient(circle, rgba(77,184,232,0.15), transparent 70%);
          pointer-events: none;
        }
        .rp-left-inner { position: relative; z-index: 1; text-align: center; max-width: 400px; }
        .rp-logo-link { display: inline-block; margin-bottom: 48px; }
        .rp-logo { height: 56px; }
        .rp-icon-box {
          width: 80px; height: 80px; border-radius: 20px; margin: 0 auto 28px;
          background: rgba(77,184,232,0.15);
          display: flex; align-items: center; justify-content: center;
        }
        .rp-left-title { font-size: 32px; font-weight: 700; color: #fff; margin-bottom: 12px; }
        .rp-left-desc { font-size: 16px; color: rgba(255,255,255,0.6); line-height: 1.6; }

        .rp-right {
          flex: 0 0 50%; display: flex; align-items: center; justify-content: center;
          padding: 40px 24px; background: #fff;
        }
        .rp-right-inner { width: 100%; max-width: 440px; }
        .rp-mobile-logo { display: none; text-align: center; margin-bottom: 32px; }
        .rp-logo-sm { height: 44px; }

        .rp-form-header { margin-bottom: 32px; }
        .rp-form-title { font-size: 30px; font-weight: 700; color: var(--navy); margin-bottom: 8px; }
        .rp-form-subtitle { font-size: 16px; color: var(--gray-500); line-height: 1.5; }

        .rp-field { margin-bottom: 20px; }
        .rp-label { display: block; font-size: 14px; font-weight: 500; color: var(--gray-900); margin-bottom: 6px; }
        .rp-input {
          width: 100%; height: 46px; padding: 0 14px; border-radius: 10px;
          border: 1px solid var(--gray-200); font-size: 15px; font-family: inherit;
          color: var(--gray-900); background: var(--gray-50); outline: none;
          transition: all 0.2s; box-sizing: border-box;
        }
        .rp-input:focus { border-color: var(--sky); background: #fff; box-shadow: 0 0 0 3px rgba(77,184,232,0.1); }
        .rp-input-wrap { position: relative; }
        .rp-input-wrap .rp-input { padding-right: 44px; }
        .rp-eye-btn { position: absolute; right: 14px; top: 13px; background: none; border: none; padding: 0; cursor: pointer; }

        .rp-submit-btn {
          display: flex; align-items: center; justify-content: center; gap: 8px;
          width: 100%; height: 48px; background: var(--green); color: #fff;
          border: none; border-radius: 10px; font-size: 16px; font-weight: 600;
          font-family: inherit; cursor: pointer;
          box-shadow: 0 4px 14px rgba(58,170,53,0.3); transition: all 0.2s;
          margin-bottom: 24px;
        }
        .rp-submit-btn:hover:not(:disabled) { background: #2e9229; }
        .rp-submit-btn:disabled { opacity: 0.7; cursor: not-allowed; }
        .rp-error { background: #FFEBEE; color: var(--red); padding: 10px 14px; border-radius: 8px; font-size: 14px; margin-bottom: 16px; }

        .rp-switch { text-align: center; font-size: 15px; color: var(--gray-500); }
        .rp-switch a { font-weight: 600; color: var(--sky); text-decoration: none; }
        .rp-switch a:hover { text-decoration: underline; }

        .rp-success { text-align: center; }
        .rp-success-icon {
          width: 64px; height: 64px; border-radius: 50%;
          background: var(--green-light); display: flex; align-items: center;
          justify-content: center; margin: 0 auto 24px;
        }
        .rp-success-title { font-size: 26px; font-weight: 700; color: var(--navy); margin-bottom: 8px; }
        .rp-success-desc { font-size: 16px; color: var(--gray-500); line-height: 1.6; margin-bottom: 32px; }

        @media (max-width: 768px) {
          .rp-wrap { flex-direction: column; }
          .rp-left { flex: none; padding: 32px 24px; }
          .rp-left-title { font-size: 24px; }
          .rp-left-desc { font-size: 14px; }
          .rp-icon-box { width: 60px; height: 60px; border-radius: 16px; margin-bottom: 20px; }
          .rp-logo-link { margin-bottom: 24px; }
          .rp-logo { height: 44px; }
          .rp-right { flex: none; padding: 32px 20px; }
        }

        @media (max-width: 480px) {
          .rp-left { display: none; }
          .rp-mobile-logo { display: block; }
          .rp-right { min-height: calc(100vh - 72px); padding: 40px 20px; }
          .rp-form-title { font-size: 26px; }
        }
      `}</style>
    </div>
  );
}
